import { type FormEvent, type ReactNode, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Input, Select } from '@/shared/ui/Input';
import { PageHeader } from '@/shared/ui/PageHeader';
import { PasswordInput } from '@/shared/ui/PasswordInput';
import { Spinner } from '@/shared/ui/Spinner';
import { ArrowLeftIcon, EyeIcon, TrashIcon } from '@/shared/ui/icons';
import { formatBytes, formatDate } from '@/shared/utils/format';

import { payrollApi } from './api';
import { EmployeeDialog, PasswordOnce } from './EmployeeDialogs';
import {
  MATCH_LABELS,
  type PayrollBatchDetail,
  type PayrollEmployee,
  type PayrollEmployeeCreated,
  type PayrollNote,
  type PayrollSlip,
  batchTitle,
  failureTitle,
  hasSlips,
  payslips,
  people,
  periodText,
  stateLabel,
  stateTone,
  withOpenFailures,
} from './types';
import {
  useAcceptBatch,
  useDiscardBatch,
  useEmployees,
  useGivePassword,
  usePayrollBatch,
  useRetryBatch,
  useSendPayroll,
  useSendTest,
  useSetPeriod,
  useUpdateSlip,
} from './usePayroll';

const dayTime = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeStyle: 'short' });
/** A date without a time (`purges_on`): read as the day it says, wherever the browser is. */
const plainDay = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeZone: 'UTC' });

const HELP = [
  <>
    Cada fila es la nómina de una persona, separada del fichero por su <strong>NIF</strong> o su{' '}
    <strong>nombre</strong>. <strong>Ver</strong> la abre en otra pestaña; queda apuntado quién la
    abrió.
  </>,
  <>
    Lo que sale <strong>Sin persona</strong> se asigna eligiendo a quién va, o se marca{' '}
    <strong>No enviar</strong>. Con un NIF que no es de nadie de la lista,{' '}
    <strong>Dar de alta</strong> añade a esa persona. Quien está de baja también sale, para su
    finiquito.
  </>,
  <>
    <strong>Enviarme una de prueba</strong> te manda una a ti, igual que le llegará a la persona.
    Nadie más recibe nada.
  </>,
  <>
    <strong>Enviar</strong> manda a cada persona solo la suya, en un PDF con su contraseña, y te
    pide la tuya antes.
  </>,
];

/**
 * One month of payslips (contract v10.53, ADR 0047 of the back): what came, what the
 * worker is doing with it, every payslip with whom it goes to, and «Enviar». The screen
 * follows the batch: while the worker has it, it asks again every two seconds.
 */
export function PayrollBatchPage() {
  const { batchId } = useParams<{ batchId: string }>();
  const batch = usePayrollBatch(batchId);
  const back = (
    <Link to="/nominas" className="text-ink-3 hover:text-ink inline-flex items-center gap-1">
      <ArrowLeftIcon size={14} /> Nóminas
    </Link>
  );

  // A refresh that fails (every 2 s while the worker has it) keeps what was there: the
  // error takes the screen only when there is nothing to show.
  if (batch.data) return <BatchScreen batch={batch.data} back={back} />;
  if (batch.isError) {
    return (
      <>
        <PageHeader title="Nóminas" back={back} />
        <ErrorMessage error={batch.error}>No se pudieron cargar las nóminas</ErrorMessage>
      </>
    );
  }
  return <Spinner variant="block" label="Cargando las nóminas" />;
}

function arrivedLine(batch: PayrollBatchDetail): string {
  const when = formatDate(dayTime, batch.received_at);
  if (batch.source === 'upload') {
    return batch.created_by ? `Subidas por ${batch.created_by} el ${when}` : `Subidas el ${when}`;
  }
  const who =
    batch.sender_name && batch.sender
      ? `${batch.sender_name} (${batch.sender})`
      : batch.sender_name || batch.sender;
  return who ? `Llegaron el ${when} de ${who}` : `Llegaron el ${when}`;
}

function BatchScreen({ batch, back }: { batch: PayrollBatchDetail; back: ReactNode }) {
  const navigate = useNavigate();
  const [discarding, setDiscarding] = useState(false);
  const discard = useDiscardBatch(batch.id);
  const canDiscard = batch.status !== 'discarded' && batch.status !== 'sending';
  const open = withOpenFailures(batch);

  const files = (batch.files ?? [])
    .filter((file) => file.kind !== 'eml')
    .map((file) => (file.size ? `${file.name} (${formatBytes(file.size)})` : file.name));
  const facts = [
    files.length > 0 ? files.join(', ') : null,
    batch.pages ? `${batch.pages} ${batch.pages === 1 ? 'página' : 'páginas'}` : null,
    batch.purged_at
      ? `ficheros borrados el ${formatDate(dayTime, batch.purged_at)}`
      : batch.purges_on
        ? `se borran el ${formatDate(plainDay, batch.purges_on)}`
        : null,
  ].filter(Boolean);

  return (
    <>
      <PageHeader
        back={back}
        title={batchTitle(batch)}
        badge={
          <Badge tone={batch.stuck ? 'danger' : stateTone(open)}>
            {batch.stuck ? 'Atascadas' : stateLabel(open)}
          </Badge>
        }
        description={arrivedLine(batch)}
        help={HELP}
        actions={
          canDiscard ? (
            <Button
              variant="ghost"
              icon={<TrashIcon size={16} />}
              onClick={() => setDiscarding(true)}
            >
              Descartar
            </Button>
          ) : null
        }
      />
      {facts.length > 0 ? (
        <p className="text-ink-3 -mt-2 text-[13px]">{facts.join(' · ')}</p>
      ) : null}

      <StatusPanel batch={batch} onDiscard={() => setDiscarding(true)} />

      {(batch.warnings ?? []).length > 0 ? (
        <Notes tone="warn" notes={batch.warnings ?? []} />
      ) : null}

      {hasSlips(batch) && !batch.purged_at ? <PeriodField batch={batch} /> : null}

      {hasSlips(batch) && (batch.items ?? []).length > 0 ? <SlipList batch={batch} /> : null}

      {hasSlips(batch) ? <SendCard batch={batch} /> : null}

      {discarding ? (
        <Dialog
          title="¿Descartar estas nóminas?"
          size="sm"
          onClose={discard.isPending ? () => undefined : () => setDiscarding(false)}
          busy={discard.isPending}
          footer={
            <>
              <Button
                variant="secondary"
                disabled={discard.isPending}
                onClick={() => setDiscarding(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                disabled={discard.isPending}
                onClick={() =>
                  discard.mutate(undefined, { onSuccess: () => void navigate('/nominas') })
                }
              >
                {discard.isPending ? 'Descartando…' : 'Descartar'}
              </Button>
            </>
          }
        >
          <p className="text-ink-2 text-[14px]">
            Se borran ya el correo, el comprimido y cada nómina separada. Queda apuntado a quién se
            mandó cada una, si ya salieron.
          </p>
          {discard.isError ? (
            <ErrorMessage error={discard.error}>No se pudieron descartar</ErrorMessage>
          ) : null}
        </Dialog>
      ) : null}
    </>
  );
}

function Notes({
  tone,
  notes,
  title,
}: {
  tone: 'warn' | 'info';
  notes: PayrollNote[];
  title?: string;
}) {
  if (notes.length === 1) {
    return (
      <Callout tone={tone} title={title}>
        {notes[0]?.message}
      </Callout>
    );
  }
  return (
    <Callout tone={tone} title={title}>
      <ul className="list-disc pl-5">
        {notes.map((note) => (
          <li key={`${note.code}-${note.message}`}>{note.message}</li>
        ))}
      </ul>
    </Callout>
  );
}

/** What the batch is waiting for, or what went wrong, with the one way out. */
function StatusPanel({ batch, onDiscard }: { batch: PayrollBatchDetail; onDiscard: () => void }) {
  const retry = useRetryBatch(batch.id);
  const accept = useAcceptBatch(batch.id);
  const retryButton = batch.can_retry ? (
    <Button size="sm" variant="secondary" disabled={retry.isPending} onClick={() => retry.mutate()}>
      {retry.isPending ? 'Pidiéndolo…' : 'Volver a intentarlo'}
    </Button>
  ) : null;
  const retryError = retry.isError ? (
    <ErrorMessage error={retry.error}>No se pudo volver a pedir</ErrorMessage>
  ) : null;

  if (batch.purged_at && batch.status !== 'discarded') {
    return (
      <Callout tone="neutral" title="Los ficheros ya se han borrado">
        Queda a quién se mandó cada una y cuándo, no las nóminas.
      </Callout>
    );
  }

  switch (batch.status) {
    case 'detecting':
    case 'preparing':
      return (
        <>
          {batch.stuck ? (
            <Callout tone="warn" title="Lleva un rato sin avanzar" action={retryButton}>
              El servidor no ha terminado de prepararlas.
            </Callout>
          ) : (
            <Card>
              <Spinner
                variant="block"
                label={
                  batch.status === 'detecting'
                    ? 'Comprobando que son las nóminas…'
                    : 'Separándolas por persona…'
                }
              />
            </Card>
          )}
          {retryError}
        </>
      );
    case 'sending':
      return (
        <>
          {batch.stuck ? (
            <Callout tone="warn" title="El envío lleva un rato sin avanzar" action={retryButton}>
              Van {batch.sent ?? 0} enviadas. Volver a intentarlo sigue con las que faltan.
            </Callout>
          ) : (
            <Callout tone="info" title="Enviándose" role="status">
              Van {batch.sent ?? 0} de {(batch.sent ?? 0) + (batch.to_send ?? 0)}. No hace falta
              esperar en esta pantalla.
            </Callout>
          )}
          {retryError}
        </>
      );
    case 'not_payroll':
      return (
        <>
          <Callout
            tone="neutral"
            title="No parece que sean las nóminas"
            action={
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" onClick={onDiscard}>
                  Descartar
                </Button>
                <Button size="sm" disabled={accept.isPending} onClick={() => accept.mutate()}>
                  {accept.isPending ? 'Preparándolas…' : 'Sí son las nóminas'}
                </Button>
              </div>
            }
          >
            {batch.detection_reason ?? 'Por lo que trae, no parecen las nóminas del mes.'}
            {batch.subject ? (
              <span className="mt-1 block">
                Asunto: <span className="text-ink-2">{batch.subject}</span>
              </span>
            ) : null}
          </Callout>
          {accept.isError ? (
            <ErrorMessage error={accept.error}>No se pudieron preparar</ErrorMessage>
          ) : null}
        </>
      );
    case 'needs_password':
      return <ZipPassword batch={batch} />;
    case 'failed':
      return (
        <>
          <Callout tone="danger" title={failureTitle(batch.error_code)} action={retryButton}>
            {batch.error}
            {batch.error_code ? <span className="text-ink-3"> ({batch.error_code})</span> : null}
            {batch.can_retry
              ? null
              : ' Súbelas otra vez con «Subir nóminas» o pide a la asesoría que las mande de nuevo.'}
          </Callout>
          {retryError}
        </>
      );
    case 'discarded':
      return (
        <Callout tone="neutral" title="Descartadas">
          Sus ficheros se borraron. Queda a quién se mandó cada una, si salió alguna.
        </Callout>
      );
    case 'sent':
      return <SentSummary batch={batch} />;
    case 'ready':
      return null;
  }
}

function SentSummary({ batch }: { batch: PayrollBatchDetail }) {
  const by = batch.send_requested_by ? ` por ${batch.send_requested_by}` : '';
  const when = batch.sent_at ? ` el ${formatDate(dayTime, batch.sent_at)}` : '';
  const sent = batch.sent ?? 0;
  // Without the ones marked «No enviar» afterwards: nobody has to chase those.
  const open = withOpenFailures(batch);
  const refused = open.refused ?? 0;
  const unknown = open.unknown ?? 0;
  return (
    <>
      <Callout
        tone={sent > 0 ? 'ok' : 'neutral'}
        title={`${payslips(sent)} ${sent === 1 ? 'enviada' : 'enviadas'}`}
      >
        {sent > 0 ? 'Enviadas' : 'Envío pedido'}
        {by}
        {when}.
      </Callout>
      {refused > 0 ? (
        <Callout
          tone="danger"
          title={refused === 1 ? 'Una no se pudo enviar' : `${refused} no se pudieron enviar`}
        >
          El servidor de correo no las aceptó: el motivo está en su fila. Corrige el correo de esa
          persona en Trabajadores y vuelve a enviar: salen solo las que faltan.
        </Callout>
      ) : null}
      {unknown > 0 ? (
        <Callout
          tone="warn"
          title={unknown === 1 ? 'Una sin confirmar' : `${unknown} sin confirmar`}
        >
          El servidor de correo cortó antes de decir si las aceptó: puede que hayan llegado.
          Pregúntale a esa persona antes de volver a enviarla.
        </Callout>
      ) : null}
    </>
  );
}

function ZipPassword({ batch }: { batch: PayrollBatchDetail }) {
  const give = useGivePassword(batch.id);
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!password) return;
    give.mutate(
      { password, remember: Boolean(batch.mailbox) && remember },
      { onSuccess: () => setPassword('') },
    );
  }

  return (
    <Card title="El comprimido tiene contraseña" description="La que manda la asesoría.">
      <form onSubmit={submit} className="flex max-w-md flex-col gap-3">
        <PasswordInput
          label="Contraseña del comprimido"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          // Not the person's own: «new-password» keeps the browser from filling theirs in.
          autoComplete="new-password"
          required
        />
        {batch.mailbox ? (
          <label className="text-ink-2 flex items-center gap-2 text-[13.5px]">
            <input
              type="checkbox"
              className="accent-accent size-4"
              checked={remember}
              onChange={(event) => setRemember(event.target.checked)}
            />
            Recordarla para el mes que viene
          </label>
        ) : null}
        {give.isError ? (
          <ErrorMessage error={give.error}>No se pudo abrir el comprimido</ErrorMessage>
        ) : null}
        <div>
          <Button type="submit" disabled={give.isPending || !password}>
            {give.isPending ? 'Abriendo…' : 'Abrir'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** The month that goes in each email's subject («Nómina de octubre de 2026»). */
function PeriodField({ batch }: { batch: PayrollBatchDetail }) {
  const setPeriod = useSetPeriod(batch.id);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(batch.period ?? '');
  const [malformed, setMalformed] = useState(false);
  const anyOut = (batch.items ?? []).some((slip) => slip.send_status != null);
  const editable = !anyOut && batch.status !== 'sending';
  const month = periodText(batch.period);

  if (editing) {
    return (
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          // Firefox and Safari on a computer have no month picker: a text field, where
          // «10/2026» is easy to type. Said in the field, not ignored.
          if (!/^\d{4}-\d{2}$/.test(value.trim())) {
            setMalformed(true);
            return;
          }
          setPeriod.mutate(value.trim(), { onSuccess: () => setEditing(false) });
        }}
      >
        <Input
          label="Mes"
          type="month"
          value={value}
          placeholder="2026-10"
          onChange={(event) => {
            setValue(event.target.value);
            setMalformed(false);
          }}
          error={malformed ? 'Escríbelo con el año y el mes así: 2026-10.' : undefined}
          required
        />
        <Button type="submit" disabled={setPeriod.isPending || !value}>
          {setPeriod.isPending ? 'Guardando…' : 'Guardar mes'}
        </Button>
        <Button variant="ghost" onClick={() => setEditing(false)} disabled={setPeriod.isPending}>
          Cancelar
        </Button>
        {setPeriod.isError ? (
          <div className="basis-full">
            <ErrorMessage error={setPeriod.error}>No se pudo cambiar el mes</ErrorMessage>
          </div>
        ) : null}
      </form>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[14px]">
      <span className="text-ink-2">
        Mes: <strong className="text-ink">{month ?? 'sin poner'}</strong>
      </span>
      {batch.period_guessed && editable ? (
        <span className="text-warn text-[13px]">Sacado del fichero: compruébalo.</span>
      ) : null}
      {!month && editable ? (
        <span className="text-warn text-[13px]">Ponlo antes de enviar: va en el asunto.</span>
      ) : null}
      {editable ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setValue(batch.period ?? '');
            setMalformed(false);
            setEditing(true);
          }}
        >
          Cambiar
        </Button>
      ) : null}
    </div>
  );
}

const SEND_STATES: Record<string, string> = {
  pending: 'En cola',
  sending: 'Enviándose',
  sent: 'Enviada',
  refused: 'No se pudo enviar',
  unknown: 'Sin confirmar',
};

function pagesText(slip: PayrollSlip): string {
  const last = slip.first_page + slip.pages - 1;
  return slip.pages > 1 ? `págs. ${slip.first_page}–${last}` : `pág. ${slip.first_page}`;
}

function slipState(slip: PayrollSlip): {
  text: string;
  tone: 'ok' | 'warn' | 'danger' | 'neutral';
} {
  if (slip.send_status === 'sent') {
    return {
      text: slip.sent_at ? `Enviada ${formatDate(dayTime, slip.sent_at)}` : 'Enviada',
      tone: 'ok',
    };
  }
  if (slip.send_status === 'refused') return { text: 'No se pudo enviar', tone: 'danger' };
  if (slip.send_status === 'unknown') return { text: 'Sin confirmar', tone: 'warn' };
  if (slip.send_status) return { text: SEND_STATES[slip.send_status] ?? '', tone: 'neutral' };
  if (slip.excluded) return { text: 'No se envía', tone: 'neutral' };
  if (!slip.employee_id) return { text: 'Sin persona', tone: 'warn' };
  if (!slip.employee_ready) return { text: 'Le falta correo o contraseña', tone: 'warn' };
  return { text: 'Por enviar', tone: 'neutral' };
}

const TONE_TEXT = {
  ok: 'text-ok',
  warn: 'text-warn',
  danger: 'text-danger',
  neutral: 'text-ink-3',
} as const;

/**
 * Every payslip of the month, in the order of the file. The same markup on a phone and on
 * a desk: a row that wraps, because each one has a select and buttons and a card list
 * would have to repeat them.
 */
function SlipList({ batch }: { batch: PayrollBatchDetail }) {
  // With those who are off the list too: the last payslip of whoever left (the finiquito)
  // goes to them, and the back takes it.
  const employees = useEmployees({ include_inactive: true });
  const update = useUpdateSlip(batch.id);
  const [adding, setAdding] = useState<PayrollSlip | null>(null);
  const [created, setCreated] = useState<PayrollEmployeeCreated | null>(null);
  const everyone = employees.data?.items ?? [];
  const items = batch.items ?? [];
  const editable = (batch.status === 'ready' || batch.status === 'sent') && !batch.purged_at;
  // The NIFs already on the list, those off it too: «Dar de alta» with one of them would be
  // a second person with the same NIF. Unknown until the list comes.
  const knownNifs = employees.data
    ? new Set(everyone.flatMap((employee) => (employee.nif ? [employee.nif.toUpperCase()] : [])))
    : null;

  return (
    <Card
      title="Las nóminas"
      description={[
        payslips(batch.slips ?? 0),
        (batch.excluded ?? 0) > 0 ? `${batch.excluded ?? 0} sin enviar a propósito` : null,
      ]
        .filter(Boolean)
        .join(' · ')}
      padded={false}
    >
      <ol className="divide-line-2 divide-y">
        {items.map((slip, index) => (
          <SlipRow
            key={slip.id}
            index={index}
            slip={slip}
            batchId={batch.id}
            employees={everyone}
            knownNifs={knownNifs}
            editable={editable}
            busy={update.isPending && update.variables?.slipId === slip.id}
            onChange={(change) => update.mutate({ slipId: slip.id, ...change })}
            onAdd={() => setAdding(slip)}
          />
        ))}
      </ol>
      {update.isError ? (
        <div className="px-4 pb-4">
          <ErrorMessage error={update.error}>No se pudo cambiar</ErrorMessage>
        </div>
      ) : null}
      {employees.isError ? (
        <div className="px-4 pb-4">
          <ErrorMessage error={employees.error}>
            No se pudo cargar la lista de trabajadores
          </ErrorMessage>
        </div>
      ) : null}
      {adding ? (
        <EmployeeDialog
          {...(adding.detected_nif ? { initialNif: adding.detected_nif } : {})}
          onClose={() => setAdding(null)}
          onCreated={(result) => {
            update.mutate({ slipId: adding.id, employee_id: result.employee.id });
            setAdding(null);
            setCreated(result);
          }}
        />
      ) : null}
      {created ? (
        <PasswordOnce
          name={created.employee.full_name}
          password={created.password}
          onClose={() => setCreated(null)}
        />
      ) : null}
    </Card>
  );
}

function SlipRow({
  index,
  slip,
  batchId,
  employees,
  knownNifs,
  editable,
  busy,
  onChange,
  onAdd,
}: {
  index: number;
  slip: PayrollSlip;
  batchId: string;
  employees: PayrollEmployee[];
  knownNifs: ReadonlySet<string> | null;
  editable: boolean;
  busy: boolean;
  onChange: (change: { employee_id?: string | null; excluded?: boolean }) => void;
  onAdd: () => void;
}) {
  const out = slip.send_status === 'sent' || slip.send_status === 'sending';
  const canChange = editable && !out;
  const state = slipState(slip);
  const nifIsNobodys =
    knownNifs !== null &&
    Boolean(slip.detected_nif) &&
    !knownNifs.has((slip.detected_nif ?? '').toUpperCase());
  // The person it has may not be in the list that came (more than 200): still an option.
  const options =
    slip.employee_id && !employees.some((each) => each.id === slip.employee_id)
      ? [
          ...employees,
          {
            id: slip.employee_id,
            full_name: slip.employee_name ?? 'Persona asignada',
          } as PayrollEmployee,
        ]
      : employees;
  const label = `Nómina ${index + 1}`;

  return (
    <li
      className="flex flex-col gap-2 px-4 py-3 md:grid md:grid-cols-[minmax(0,12rem)_minmax(0,1fr)_minmax(0,11rem)_auto] md:items-center md:gap-4"
      aria-label={label}
    >
      <div className="min-w-0 text-[13.5px]">
        <p className="text-ink truncate font-medium">
          {index + 1}. {slip.source_name ?? 'Nómina'}
        </p>
        <p className="text-ink-3 text-[12.5px]">
          {pagesText(slip)}
          {slip.detected_nif ? ` · NIF ${slip.detected_nif}` : ' · sin NIF leído'}
          {slip.scanned ? ' · escaneada' : ''}
        </p>
      </div>

      <div className="min-w-0">
        {canChange ? (
          <Select
            label={`A quién va la ${label.toLowerCase()}`}
            className="w-full"
            value={slip.employee_id ?? ''}
            disabled={busy || slip.excluded}
            onChange={(event) => onChange({ employee_id: event.target.value || null })}
            hint={
              slip.employee_id
                ? [
                    slip.employee_email ?? 'sin correo',
                    slip.matched_by ? MATCH_LABELS[slip.matched_by] : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : undefined
            }
          >
            <option value="">Sin persona</option>
            {options.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.full_name}
                {employee.active === false ? ' (de baja)' : ''}
                {employee.nif ? ` · ${employee.nif}` : ''}
              </option>
            ))}
          </Select>
        ) : (
          <p className="text-[13.5px]">
            <span className="text-ink">{slip.employee_name ?? 'Sin persona'}</span>
            {slip.employee_email ? (
              <span className="text-ink-3 block text-[12.5px]">{slip.employee_email}</span>
            ) : null}
          </p>
        )}
      </div>

      <div className={`text-[13px] ${TONE_TEXT[state.tone]}`}>
        {state.text}
        {slip.send_error && (slip.send_status === 'refused' || slip.send_status === 'unknown') ? (
          <span className="text-ink-3 block text-[12.5px]">{slip.send_error}</span>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-1.5 md:justify-end">
        {slip.has_pdf ? (
          <a
            href={payrollApi.slipPdfUrl(batchId, slip.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-ink-2 hover:bg-paper-2 hover:text-ink inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium"
            aria-label={`Ver la ${label.toLowerCase()}`}
          >
            <EyeIcon size={15} /> Ver
          </a>
        ) : null}
        {canChange && nifIsNobodys && !slip.employee_id && !slip.excluded ? (
          <Button size="sm" variant="secondary" disabled={busy} onClick={onAdd}>
            Dar de alta
          </Button>
        ) : null}
        {canChange ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => onChange({ excluded: !slip.excluded })}
          >
            {slip.excluded ? 'Enviar también' : 'No enviar'}
          </Button>
        ) : null}
      </div>
    </li>
  );
}

/** Why «Enviar» cannot go yet: each blocker with where to fix it. */
function Blockers({ blockers }: { blockers: PayrollNote[] }) {
  return (
    <ul className="flex flex-col gap-1 text-[13.5px]">
      {blockers.map((note) => (
        <li key={`${note.code}-${note.message}`} className="text-warn">
          {note.message}
          <BlockerWayOut code={note.code} />
        </li>
      ))}
    </ul>
  );
}

/**
 * What to do, after the back's sentence, when it is not on this screen or not obvious. A
 * payslip to assign again («Sin persona», `nif_changed`) is in the list just above.
 */
function BlockerWayOut({ code }: { code: string }) {
  const toEmployees = (text: string) => (
    <Link to="/nominas/trabajadores" className="link">
      {text}
    </Link>
  );
  switch (code) {
    case 'no_email':
    case 'no_password':
      return <> {toEmployees('Ir a Trabajadores')}</>;
    case 'nif_changed':
      return <> Si el NIF nuevo está mal, corrígelo en {toEmployees('Trabajadores')}.</>;
    case 'nif_conflict':
      return (
        <>
          {' '}
          Si las dos son suyas: marca una «No enviar» y envía; después, «Enviar también» en esa y
          envía otra vez.
        </>
      );
    default:
      return null;
  }
}

function TestLine({ batch }: { batch: PayrollBatchDetail }) {
  const test = batch.last_test;
  if (!test) return null;
  if (test.status === 'sending') {
    return (
      <p className="text-ink-3 text-[13.5px]" role="status">
        Mandando la prueba a {test.to}…
      </p>
    );
  }
  if (test.status === 'sent') {
    return (
      <p className="text-ink-2 text-[13.5px]" role="status">
        La prueba ha salido a <strong>{test.to}</strong>
        {test.employee_name ? <>, con la nómina de {test.employee_name}</> : null}.{' '}
        {test.own ? (
          'Se abre con tu contraseña de nóminas.'
        ) : test.password ? (
          <>
            Se abre con la contraseña de prueba{' '}
            <span className="text-ink font-mono select-all">{test.password}</span>.
          </>
        ) : null}
      </p>
    );
  }
  return (
    <p className="text-danger text-[13.5px]" role="alert">
      La prueba no salió{test.error ? `: ${test.error}` : '.'}
    </p>
  );
}

function SendCard({ batch }: { batch: PayrollBatchDetail }) {
  const test = useSendTest(batch.id);
  const send = useSendPayroll(batch.id);
  const [confirming, setConfirming] = useState(false);
  // While they go out the back says «Todavía no están listas»: true for «Enviar», wrong to
  // read above «Van 3 de 21».
  const blockers = (batch.blockers ?? []).filter(
    (note) => note.code !== 'nothing' && !(batch.status === 'sending' && note.code === 'not_ready'),
  );
  const cannotSend = blockers.some(
    (note) => note.code === 'locked' || note.code === 'no_mailbox' || note.code === 'purged',
  );
  const allOut = (batch.to_send ?? 0) === 0 && blockers.length === 0;
  const unknownAgain = (batch.items ?? []).filter(
    (slip) => slip.send_status === 'unknown' && !slip.excluded && slip.employee_id,
  ).length;
  const canTest = !cannotSend && batch.status !== 'sending' && !batch.purged_at;
  const canSend = blockers.length === 0 && (batch.to_send ?? 0) > 0 && batch.status !== 'sending';
  const sendLabel = batch.status === 'sent' ? 'Enviar las que faltan' : 'Enviar';

  if (allOut && batch.status === 'sent' && !batch.last_test) return null;

  return (
    <Card
      title="Enviar"
      description={
        batch.sends_from
          ? `Salen desde ${batch.sends_from}, una a cada persona, en un PDF con su contraseña. No se guarda copia en Enviados.`
          : 'Una a cada persona, en un PDF con su contraseña. No se guarda copia en Enviados.'
      }
    >
      {blockers.length > 0 ? <Blockers blockers={blockers} /> : null}
      <TestLine batch={batch} />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          disabled={!canTest || test.isPending || batch.last_test?.status === 'sending'}
          onClick={() => test.mutate()}
        >
          {test.isPending ? 'Pidiéndola…' : 'Enviarme una de prueba'}
        </Button>
        {(batch.to_send ?? 0) > 0 || batch.status === 'ready' ? (
          <Button disabled={!canSend} onClick={() => setConfirming(true)}>
            {canSend ? `${sendLabel} ${payslips(batch.to_send ?? 0)}` : sendLabel}
          </Button>
        ) : null}
      </div>
      {test.isError ? (
        <ErrorMessage error={test.error}>No se pudo pedir la prueba</ErrorMessage>
      ) : null}
      {send.isError && !confirming ? (
        <ErrorMessage error={send.error}>No se pudieron enviar</ErrorMessage>
      ) : null}

      {confirming ? (
        <Dialog
          title={`Vas a enviar ${payslips(batch.to_send ?? 0)} a ${people(batch.people_to_send ?? 0)}`}
          size="sm"
          onClose={send.isPending ? () => undefined : () => setConfirming(false)}
          busy={send.isPending}
          footer={
            <>
              <Button
                variant="secondary"
                disabled={send.isPending}
                onClick={() => setConfirming(false)}
              >
                Cancelar
              </Button>
              <Button
                disabled={send.isPending}
                onClick={() =>
                  send.mutate(batch.to_send ?? 0, {
                    onSuccess: () => setConfirming(false),
                    onError: () => setConfirming(false),
                  })
                }
              >
                {send.isPending ? 'Enviando…' : 'Enviar'}
              </Button>
            </>
          }
        >
          <div className="text-ink-2 flex flex-col gap-2 text-[14px]">
            <p>
              Cada una a su correo, solo la suya, con su contraseña
              {periodText(batch.period) ? (
                <> y el asunto «Nómina de {periodText(batch.period)}»</>
              ) : null}
              . Te pedirá tu contraseña.
            </p>
            {unknownAgain > 0 ? (
              <p className="text-warn">
                {unknownAgain === 1
                  ? 'Incluye una que no se sabe si llegó: puede que a esa persona le llegue dos veces.'
                  : `Incluye ${unknownAgain} que no se sabe si llegaron: puede que a alguien le llegue dos veces.`}
              </p>
            ) : null}
            {batch.period_guessed ? (
              <p className="text-warn">
                El mes lo sacó la aplicación del fichero: compruébalo antes.
              </p>
            ) : null}
          </div>
        </Dialog>
      ) : null}
    </Card>
  );
}
